import { RenderLayerMask } from './babylon/RenderLayer.js';
import { LightType } from './babylon/light-manager/types/LightType.js';
import { ColorMode } from './babylon/light-manager/light-system/debug_visualizers/LightVisualizer.js';
import { is_polygon_offset_bias } from './babylon/light-manager/light-system/shadows/ShadowController.js';
import { IVector2 } from './api/IVector2.js';
import { IVector3 } from './api/IVector3.js';
import { IVector4 } from './api/IVector4.js';
import { IColor } from './api/IColor.js';
import { IColor4 } from './api/IColor4.js';
import { IMatrix4 } from './api/IMatrix4.js';
import { Random } from './api/Random.js';
import { IRay } from './api/IRay.js';
import { IBoundingBox } from './api/IBoundingBox.js';
import { ITransformedBoundingBox } from './api/ITransformedBoundingBox.js';
import { IBoundingSphere } from './api/IBoundingSphere.js';
export { IVector2, IVector3, IVector4, IColor, IColor4, IMatrix4 };
export { IBoundingBox, ITransformedBoundingBox, IBoundingSphere };
export { Random };
export { RenderLayerMask };
export { ColorMode, LightType, is_polygon_offset_bias };
/**
 * The six main directions of the 3D scene, describing from which side you are looking at it.
 */
export var CardinalDirection;
(function (CardinalDirection) {
    CardinalDirection[CardinalDirection["FRONT"] = 0] = "FRONT";
    CardinalDirection[CardinalDirection["BACK"] = 1] = "BACK";
    CardinalDirection[CardinalDirection["LEFT"] = 2] = "LEFT";
    CardinalDirection[CardinalDirection["RIGHT"] = 3] = "RIGHT";
    CardinalDirection[CardinalDirection["BOTTOM"] = 4] = "BOTTOM";
    CardinalDirection[CardinalDirection["TOP"] = 5] = "TOP";
})(CardinalDirection || (CardinalDirection = {}));
/**
 * Enum describing presets for draco decoder sources
 * These are prefered over custom urls since they allow for automatic updating of urls with viewer version
 */
export var DracoDecoderSource;
(function (DracoDecoderSource) {
    /**
     * Should only be used when ikea.com is the origin, prefered when it is.
     */
    DracoDecoderSource[DracoDecoderSource["IKEA_COM"] = 0] = "IKEA_COM";
    /**
     * Can be used with any origin but might have worse performance than named counterparts, prefered for dev environments
     */
    DracoDecoderSource[DracoDecoderSource["UNIVERSAL"] = 1] = "UNIVERSAL";
})(DracoDecoderSource || (DracoDecoderSource = {}));
export { IRay };
export var CameraMode;
(function (CameraMode) {
    CameraMode[CameraMode["PERSPECTIVE"] = 0] = "PERSPECTIVE";
    CameraMode[CameraMode["ORTHOGRAPHIC"] = 1] = "ORTHOGRAPHIC";
})(CameraMode || (CameraMode = {}));
export var CameraTransitionPreset;
(function (CameraTransitionPreset) {
    CameraTransitionPreset[CameraTransitionPreset["NONE"] = 0] = "NONE";
    CameraTransitionPreset[CameraTransitionPreset["SMOOTH"] = 1] = "SMOOTH";
    CameraTransitionPreset[CameraTransitionPreset["SMOOTH_FAST"] = 2] = "SMOOTH_FAST";
    CameraTransitionPreset[CameraTransitionPreset["LINEAR"] = 3] = "LINEAR";
    CameraTransitionPreset[CameraTransitionPreset["LINEAR_FAST"] = 4] = "LINEAR_FAST";
})(CameraTransitionPreset || (CameraTransitionPreset = {}));
export var ExposurePreset;
(function (ExposurePreset) {
    ExposurePreset["HOME_INTERIOR"] = "Home Interior";
    ExposurePreset["BRIGHTLY_LIT_OFFICE"] = "Brightly Lit Office";
    ExposurePreset["OUTDOOR_HEAVY_OVERCAST"] = "Outdoor Heavy Overcast";
    ExposurePreset["OUTDOOR_CLEAR_DAY"] = "Outdoor Clear Day";
})(ExposurePreset || (ExposurePreset = {}));
export var TransitionState;
(function (TransitionState) {
    TransitionState[TransitionState["NONE"] = 0] = "NONE";
    TransitionState[TransitionState["STARTED"] = 1] = "STARTED";
    TransitionState[TransitionState["CONTINUE"] = 2] = "CONTINUE";
    TransitionState[TransitionState["COMPLETED"] = 3] = "COMPLETED";
})(TransitionState || (TransitionState = {}));
/**
 * Base class that can be implemented for when writing custom camera transitions
 */
export class CameraTransition {
    constructor(from_camera, to_camera, engine_camera) {
        this.from_camera = from_camera;
        this.to_camera = to_camera;
        this.engine_camera = engine_camera;
    }
    /**
     * This function updates the projection matrix of the transition_camera so that
     * near and far planes are spread enought to see anything that the from and to cameras could have seen.
     * @param aspect_ratio current aspect ratio
     * @returns a new projection matrix with updated near and far compared to transition_camera
     */
    fix_interpolated_projection(aspect_ratio) {
        // This is a dummy implementation, CameraTransition.ts has the real default implementation
        return this.transition_camera.get_projection_matrix(aspect_ratio);
    }
}
export var AOQualityPreset;
(function (AOQualityPreset) {
    AOQualityPreset[AOQualityPreset["MOBILE_LOW"] = 0] = "MOBILE_LOW";
    AOQualityPreset[AOQualityPreset["MOBILE_MEDIUM"] = 1] = "MOBILE_MEDIUM";
    AOQualityPreset[AOQualityPreset["MOBILE_HIGH"] = 2] = "MOBILE_HIGH";
    AOQualityPreset[AOQualityPreset["DESKTOP_LOW"] = 3] = "DESKTOP_LOW";
    AOQualityPreset[AOQualityPreset["DESKTOP_MEDIUM"] = 4] = "DESKTOP_MEDIUM";
    AOQualityPreset[AOQualityPreset["DESKTOP_HIGH"] = 5] = "DESKTOP_HIGH";
    AOQualityPreset[AOQualityPreset["SCREENSHOT"] = 6] = "SCREENSHOT";
    AOQualityPreset[AOQualityPreset["DESKTOP_DEFAULT"] = 4] = "DESKTOP_DEFAULT";
    AOQualityPreset[AOQualityPreset["MOBILE_DEFAULT"] = 1] = "MOBILE_DEFAULT";
})(AOQualityPreset || (AOQualityPreset = {}));
export var LineType;
(function (LineType) {
    LineType[LineType["SOLID"] = 0] = "SOLID";
    LineType[LineType["DASHED"] = 1] = "DASHED";
})(LineType || (LineType = {}));
export var RenderLayerAllocation;
(function (RenderLayerAllocation) {
    RenderLayerAllocation[RenderLayerAllocation["IPEX_CORE"] = 0] = "IPEX_CORE";
    RenderLayerAllocation[RenderLayerAllocation["IPEX_KVADRAT"] = 1] = "IPEX_KVADRAT";
    RenderLayerAllocation[RenderLayerAllocation["IPEX_SPACE"] = 2] = "IPEX_SPACE";
    RenderLayerAllocation[RenderLayerAllocation["IPEX_PLANNER"] = 3] = "IPEX_PLANNER";
})(RenderLayerAllocation || (RenderLayerAllocation = {}));
export class DebugProfilingInformation {
    constructor() {
        this.engine_update_id = 0;
        this.num_api_objects = 0;
        this.num_api_materials = 0;
        this.num_babylon_materials = 0;
        this.num_babylon_meshes = 0;
        this.num_babylon_transform_nodes = 0;
        this.num_babylon_cameras = 0;
        this.num_babylon_template_meshes = 0;
    }
}
/**
 * Viewer has some experimental features that can be enabled here. With time, options might go away as they become standard or are removed.
 * Features here might have either performance problems (such that they should not be enabled by default) or are not fully tested yet.
 */
export class ViewerOptionalFeatures {
    constructor() {
        /**
         * This flag lets you decide if you want to utilize a float framebuffer for the Viewer, when supported.
         * This flag is left for testing and debugging purposes, and can be used as a last resort performance gain on very low-end devices.
         * Float framebuffers are required for bloom and correct tonemapping.
         * Default value: true (enabled)
         */
        this.feature_gate_use_float_framebuffer = true;
        /**
         * This flag is solely left for testing purposes.
         * Use it to emulate a device with only WebGL1 support.
         * DO NOT USE IN PRODUCTION
         * Default value: false (WebGL2 is used when supported)
         */
        this.feature_gate_disable_webgl2_support = false;
        /**
         * Enables the GPU profiler in the Viewer, for testing purposes only.
         * Do not enable in production environments.
         * Default value: false (disabled)
         */
        this.debug_enable_gpu_profiler = false;
        this.feature_gate_material_objects = false;
        /**
         * Use this flag to tell the Viewer to not create its default View.
         * This will make some endpoints on the Viewer error if used.
         * Default value: false (default view is created)
         */
        this.no_default_view = false;
    }
}
export var ScreenshotMimeType;
(function (ScreenshotMimeType) {
    ScreenshotMimeType["PNG"] = "image/png";
    ScreenshotMimeType["JPEG"] = "image/jpeg";
})(ScreenshotMimeType || (ScreenshotMimeType = {}));
/**
 * OBS, Animations are a prototype and may be subject to change.
 */
export var AnimationProperty;
(function (AnimationProperty) {
    AnimationProperty["POSITION"] = "position";
    AnimationProperty["ROTATION"] = "rotationQuaternion";
    AnimationProperty["SCALE"] = "scaling";
})(AnimationProperty || (AnimationProperty = {}));
/**
 * OBS, Animations are a prototype and may be subject to change.
 */
export var AnimationInterpolationFunction;
(function (AnimationInterpolationFunction) {
    AnimationInterpolationFunction[AnimationInterpolationFunction["LINEAR"] = 0] = "LINEAR";
    AnimationInterpolationFunction[AnimationInterpolationFunction["CUBIC"] = 1] = "CUBIC";
})(AnimationInterpolationFunction || (AnimationInterpolationFunction = {}));
//# sourceMappingURL=api.js.map